import { Alert, Button, Group, Modal, Select, SimpleGrid, Stack, Table, Text, TextInput } from "@mantine/core";
import { IconSearch } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import GeoJSON from "ol/format/GeoJSON";
import OlMap from "ol/Map";
import TileLayer from "ol/layer/Tile";
import VectorLayer from "ol/layer/Vector";
import XYZ from "ol/source/XYZ";
import VectorSource from "ol/source/Vector";
import { Fill, Stroke, Style } from "ol/style";
import View from "ol/View";
import { useEffect, useRef, useState } from "react";

import { http } from "../api/http";
import { useT } from "../i18n/script";

interface Feature { type: "Feature"; geometry: object | null; properties: Record<string, unknown> }

function ParcelPreview({ feature }: { feature: Feature }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!ref.current || !feature.geometry) return;
    const src = new VectorSource({ features: new GeoJSON().readFeatures(feature, { featureProjection: "EPSG:3857" }) });
    const map = new OlMap({
      target: ref.current, controls: [],
      layers: [new TileLayer({ source: new XYZ({ url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}", maxZoom: 19 }) }),
        new VectorLayer({ source: src, style: new Style({ stroke: new Stroke({ color: "#f5d90a", width: 2.5 }), fill: new Fill({ color: "rgba(245,217,10,0.12)" }) }) })],
      view: new View(),
    });
    map.getView().fit(src.getExtent()!, { padding: [30, 30, 30, 30], maxZoom: 18 });
    return () => map.setTarget(undefined);
  }, [feature]);
  return <div ref={ref} style={{ height: 240, borderRadius: 10, overflow: "hidden" }} />;
}

/** Pretraga parcele u centralnom katastru RUGIPP (opština → KO → broj/podbroj), uz broj predmeta za evidenciju. */
export function RugippSearch({ opened, onClose, caseNumber, initial }: {
  opened: boolean; onClose: () => void; caseNumber?: string; initial?: { opstina?: string; broj?: string; podbroj?: string };
}) {
  const { t } = useT();
  const [op, setOp] = useState<string | null>(initial?.opstina ?? "25");
  const [ko, setKo] = useState<string | null>(null);
  const [broj, setBroj] = useState(initial?.broj ?? "");
  const [podbroj, setPodbroj] = useState(initial?.podbroj ?? "0");
  const [pred, setPred] = useState(caseNumber ?? "");
  const [res, setRes] = useState<Feature | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const ops = useQuery({ queryKey: ["rugipp-op"], enabled: opened, queryFn: () => http.get<{ sifra: string; naziv: string }[]>("/api/v1/cadastre/rugipp/opstine") });
  const kos = useQuery({ queryKey: ["rugipp-ko", op], enabled: opened && !!op, retry: false,
    queryFn: () => http.get<{ sifra: string; naziv: string }[]>("/api/v1/cadastre/rugipp/ko", { opstina: op }) });
  async function search() {
    setBusy(true); setErr(null); setRes(null);
    try {
      setRes(await http.get<Feature>("/api/v1/cadastre/rugipp/parcela", { opstina: op, sifra_ko: ko, broj, podbroj: podbroj || "0", broj_predmeta: pred }));
    } catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
  }
  const props = Object.entries(res?.properties ?? {}).filter(([, v]) => v !== null && v !== "" && typeof v !== "object");
  return (
    <Modal opened={opened} onClose={onClose} title={t("RUGIPP katastar — pretraga parcele")} size="lg">
      <Stack>
        {kos.error && <Alert color="orange">{(kos.error as Error).message}</Alert>}
        <SimpleGrid cols={2}>
          <Select label={t("Opština")} searchable value={op} onChange={(v) => { setOp(v); setKo(null); }}
            data={(ops.data ?? []).map((o) => ({ value: o.sifra, label: o.naziv }))} />
          <Select label={t("Katastarska opština")} searchable value={ko} onChange={setKo} disabled={!kos.data}
            data={(kos.data ?? []).map((k) => ({ value: k.sifra, label: k.naziv }))} />
          <TextInput label={t("Broj")} value={broj} onChange={(e) => setBroj(e.currentTarget.value.replace(/\D/g, ""))} />
          <TextInput label={t("Podbroj")} value={podbroj} onChange={(e) => setPodbroj(e.currentTarget.value.replace(/\D/g, ""))} />
        </SimpleGrid>
        <TextInput label={t("Broj predmeta")} value={pred} onChange={(e) => setPred(e.currentTarget.value)} required />
        <Group justify="flex-end">
          <Button leftSection={<IconSearch size={16} />} loading={busy} disabled={!op || !ko || !broj || !pred} onClick={search}>{t("Pretraži")}</Button>
        </Group>
        {err && <Alert color="red">{err}</Alert>}
        {res && (
          <>
            {res.geometry ? <ParcelPreview feature={res} /> : <Text size="sm" c="dimmed">{t("Parcela nema geometriju")}</Text>}
            <Table fz="sm" striped withTableBorder>
              <Table.Tbody>
                {props.map(([k, v]) => <Table.Tr key={k}><Table.Td c="dimmed" w={200}>{k}</Table.Td><Table.Td>{String(v)}</Table.Td></Table.Tr>)}
              </Table.Tbody>
            </Table>
          </>
        )}
      </Stack>
    </Modal>
  );
}
